import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const advantages = [
  {
    title: "Automatic Fire Detection and Response",
    desc: "Due to their automated fire detection and response capabilities, kitchen fire suppression systems ensure that fires are stopped before any serious threats of accidents or structural damage.",
    icon: "ri-sensor-line",
  },
  {
    title: "Less Interruption to Business Activities",
    desc: "With a system that can immediately detect and extinguish kitchen fires, you will not have to disrupt all business operations whenever a fire occurs. The system can safely put out fires in specific areas of the kitchen without affecting other cooking equipment and processes.",
    icon: "ri-restaurant-2-line",
  },
  {
    title: "Protects Staff and Equipment",
    desc: "Kitchen fire suppression systems have hoods at the top to automatically drive out smoke from the kitchen, significantly reducing the chances of accidents to workers whenever fires start.",
    icon: "ri-user-heart-line",
  },
  {
    title: "Reduces Food Wastage",
    desc: "The prompt and effective response of kitchen fire suppression systems also reduces food wastage since not much will be destroyed in the fire.",
    icon: "ri-leaf-line",
  },
];

const selectionFactors = [
  { label: "Fire Extinguishing Agent", desc: "Wet chemical is most preferred as it can effectively extinguish a wide range of kitchen fires." },
  { label: "Coverage Area", desc: "Ensure the system properly covers all the potential fire threats including deep fryers, chip pans and hobs." },
  { label: "System Size & Performance", desc: "Look into the size and performance of the system to match the scale of your kitchen operations." },
  { label: "Type of Fires", desc: "Consider if the system needs to also handle electrical fires in addition to cooking oil fires." },
];

export default function KitchenFireSuppressionPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Kitchen Fire Suppression Systems Singapore",
            description:
              "What restaurant owners need to know about kitchen fire suppression systems in Singapore — how they work, their advantages, and how to choose the right system for your restaurant.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/60dc16d5-ebff-40d9-b758-ac886b2c67e2_compressed_dreamstime_m_104980760.webp"
              alt="Fire blanket safety equipment"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Kitchen Fire Suppression Systems</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Restaurant Fire Safety
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Kitchen Fire Suppression <span className="text-primary-400">Systems</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Restaurants often present higher risks of fire emergencies. Studies reveal that more than half of the reported cases of restaurant fires are caused by cooking equipment. Here is what every restaurant owner needs to know.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Why It Matters
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why Restaurants Need Kitchen Fire Suppression Systems
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Restaurants often present higher risks of fire emergencies mainly because fire is the key medium used in a majority of food preparation processes. Studies reveal that more than half of the reported cases of restaurant fires are caused by cooking equipment.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Compared to other fire suppression alternatives like fire blankets and fire extinguishers, kitchen fire suppression systems are proven to be more effective. The main reason for that is because they can automatically activate prompt and effective response, thereby putting out fires safely and faster.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed">
                To get the most of this system in your restaurant, it is important that you also know how it operates, its applications, benefits and how to choose the right one.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/572b3c84-e88f-47cd-8a1a-d673ee7ceaea_compressed_c248927c-5858-4c5a-b5ec-5af810528d19.webp"
                alt="Green EXIT sign and emergency lights installed on dark wall near window"
                title="Ezzogenics exit sign and emergency lighting installation"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                The Technology
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                How a Kitchen Fire Suppression <span className="text-primary-500">System Works</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div className="space-y-5">
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-2">Installation Location</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        A kitchen fire suppression system is installed in the cooker hood or inside the cupboards in the kitchen, which also helps with conserving space. The system is aimed at protecting cooking equipment like deep fryers, chip pans, hobs and kitchen staff during fire emergencies.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-2">Heat Detection & Activation</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        The system is designed with heat sensors programmed to detect any abnormal temperatures in the kitchen. Whenever a fire is detected, the sensors will trigger the system's nozzles to discharge wet chemicals for extinguishing the flames.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-2">Wet Chemical Suppression</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        The wet chemicals in the system are carefully engineered to immediately blanket the flames and suck oxygen from the burning materials, forcing the fire to stop. The system will also trip, cutting the gas supply to the cooking appliances that instantly eliminates the fuel source.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-accent-50 border border-accent-200/70 rounded-2xl p-8 flex flex-col gap-4">
                <i className="ri-restaurant-2-line text-4xl text-accent-500 block"></i>
                <h3 className="font-heading text-xl font-bold text-foreground-950">Key System Features</h3>
                <ul className="space-y-3">
                  {[
                    "Installed in cooker hood or kitchen cupboards — space-saving",
                    "Protects deep fryers, chip pans, hobs and kitchen equipment",
                    "Automated heat sensors for immediate detection",
                    "Wet chemical discharge smothers flames and cuts oxygen",
                    "Automatically trips gas supply to eliminate fuel source",
                    "Hood system automatically drives out smoke",
                    "Localised response — does not interrupt other cooking stations",
                  ].map((feat) => (
                    <li key={feat} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-200 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-accent-700 text-xs"></i>
                      </div>
                      <span className="text-foreground-700 text-sm leading-relaxed">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Why Install One
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Advantages of Kitchen Fire <span className="text-primary-500">Suppression Systems</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
            {advantages.map((a) => (
              <div key={a.title} className="bg-background-100 rounded-2xl p-6 border border-background-200/70 flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className={`${a.icon} text-primary-600 text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{a.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Choosing the Right System */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Buyer's Guide
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Choosing Kitchen Fire Suppression Systems <span className="text-primary-500">for Your Restaurant</span>
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                Although kitchen fire suppression systems are undoubtedly the most essential fire protection systems for restaurants, do not just buy any system that you come across in the market. Consider these key factors.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto">
              {selectionFactors.map((f) => (
                <div key={f.label} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 flex-shrink-0"></div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 text-base mb-1">{f.label}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-accent-50 border border-accent-200/70 rounded-2xl p-5 flex items-start gap-4 max-w-3xl mx-auto">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-information-line text-accent-600 text-lg"></i>
              </div>
              <p className="text-foreground-700 text-sm leading-relaxed">
                If you are not familiar with kitchen fire suppression systems, choosing the right one for your restaurant could be challenging. Engaging a fire safety professional can provide the necessary assistance to enable you get the most suitable and efficient kitchen fire suppression system for your restaurant without a lot of stress.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Get Quality Kitchen Fire Suppression Systems in Singapore
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
            Ezzogenics offers quality fire protection systems, fire safety products and QP consultancy services for restaurants and other commercial establishments in Singapore.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Get Expert Advice
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              View Our Services
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}